import type { Candle } from '../types';

const DEFAULT_FRAME_SIZE = 64;
const DEFAULT_FRAME_MULTIPLIER: 1 | 1.5 | 2 = 1.5;
const DEFAULT_SOURCE_MODE: 'highLow' | 'openClose' = 'highLow';
const DEFAULT_ADD_EXTENDED_LINES = true;
const DEFAULT_EXTRA_LINES_COUNT: 2 | 4 | 6 = 4;

export type MurreyMathLevelKind = 'major' | 'range' | 'pivot' | 'weak' | 'extended';

export interface MurreyMathLevel {
  key: string;
  label: string;
  price: number;
  kind: MurreyMathLevelKind;
  isExtended: boolean;
}

export interface MurreyMathLinesV6OverlayResult {
  levels: MurreyMathLevel[];
  zeroEight?: number;
  oneEight?: number;
  threeEight?: number;
  fiveEight?: number;
  sevenEight?: number;
  eightEight?: number;
  nearestLabel?: string;
  nearestPrice?: number;
  info: string;
}

export interface ComputeMurreyMathLinesV6OverlayParams {
  candles: Candle[];
  symbol: string;
  frameSize?: number;
  frameMultiplier?: 1 | 1.5 | 2;
  sourceMode?: 'highLow' | 'openClose';
  addExtendedLines?: boolean;
  extraLinesCount?: 2 | 4 | 6;
}

interface MurreyCore {
  zeroEight: number;
  eightEight: number;
  increment: number;
}

function clampFrameSize(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_FRAME_SIZE;
  }

  const roundedToFour = Math.round(value / 4) * 4;
  return Math.max(8, Math.min(256, roundedToFour));
}

function normalizeExtraLinesCount(value: number | undefined): 2 | 4 | 6 {
  if (value === 4 || value === 6) {
    return value;
  }
  return 2;
}

function levelKind(index: number): MurreyMathLevelKind {
  if (index === 0 || index === 4 || index === 8) {
    return 'major';
  }
  if (index === 2 || index === 6) {
    return 'pivot';
  }
  if (index === 3 || index === 5) {
    return 'range';
  }
  return 'weak';
}

function inferPricePrecision(candles: Candle[]): number {
  let maxPrecision = 0;
  const sampleSize = Math.min(candles.length, 200);

  for (let index = candles.length - sampleSize; index < candles.length; index += 1) {
    const candle = candles[index];
    if (!candle) {
      continue;
    }

    const values = [candle.open, candle.high, candle.low, candle.close];
    for (const value of values) {
      const text = value.toString();
      const decimalIndex = text.indexOf('.');
      if (decimalIndex < 0) {
        continue;
      }
      const precision = text.length - decimalIndex - 1;
      if (precision > maxPrecision) {
        maxPrecision = precision;
      }
    }
  }

  return Math.max(2, Math.min(8, maxPrecision));
}

function computeMurreyCore(frameHigh: number, frameLow: number): MurreyCore | null {
  if (!Number.isFinite(frameHigh) || !Number.isFinite(frameLow) || frameHigh <= frameLow) {
    return null;
  }

  const nHigh = frameHigh;
  const nLow = frameLow;
  const murreyRange = nHigh - nLow;
  if (murreyRange <= 0) {
    return null;
  }

  const fractal =
    nHigh > 25_000
      ? 100_000
      : nHigh > 2_500
        ? 10_000
        : nHigh > 250
          ? 1_000
          : nHigh > 25
            ? 100
            : nHigh > 6.25
              ? 12.5
              : nHigh > 3.125
                ? 6.25
                : nHigh > 1.5625
                  ? 3.125
                  : nHigh > 0.390625
                    ? 1.5625
                    : 0.1953125;

  const sum = Math.floor(Math.log(fractal / murreyRange) / Math.log(2));
  const octave = fractal * Math.pow(0.5, sum);
  if (!Number.isFinite(octave) || octave <= 0) {
    return null;
  }

  const minimum = Math.floor(nLow / octave) * octave;
  const maximum = minimum + octave > nHigh ? minimum + octave : minimum + 2 * octave;

  const band = maximum - minimum;
  if (!Number.isFinite(band) || band <= 0) {
    return null;
  }

  const t2 =
    nLow >= (3 * band) / 16 + minimum && nHigh <= (9 * band) / 16 + minimum
      ? minimum + band / 2
      : 0;
  const t1 =
    nLow >= minimum - band / 8 && nHigh <= (5 * band) / 8 + minimum && t2 === 0
      ? minimum + band / 2
      : 0;
  const t4 =
    nLow >= minimum + (7 * band) / 16 && nHigh <= (13 * band) / 16 + minimum
      ? minimum + (3 * band) / 4
      : 0;
  const t5 =
    nLow >= minimum + (3 * band) / 8 && nHigh <= (9 * band) / 8 + minimum && t4 === 0
      ? maximum
      : 0;
  const t3 =
    nLow >= minimum + band / 8 &&
    nHigh <= (7 * band) / 8 + minimum &&
    t1 === 0 &&
    t2 === 0 &&
    t4 === 0 &&
    t5 === 0
      ? minimum + (3 * band) / 4
      : 0;
  const t6 = t1 + t2 + t3 + t4 + t5 === 0 ? maximum : 0;
  const top = t1 + t2 + t3 + t4 + t5 + t6;

  const b1 = t1 > 0 ? minimum : 0;
  const b2 = t2 > 0 ? minimum + band / 4 : 0;
  const b3 = t3 > 0 ? minimum + band / 4 : 0;
  const b4 = t4 > 0 ? minimum + band / 2 : 0;
  const b5 = t5 > 0 ? minimum + band / 2 : 0;
  const b6 = top > 0 && b1 + b2 + b3 + b4 + b5 === 0 ? minimum : 0;
  const bottom = b1 + b2 + b3 + b4 + b5 + b6;

  const increment = (top - bottom) / 8;
  if (!Number.isFinite(increment) || increment <= 0) {
    return null;
  }

  return {
    zeroEight: bottom,
    eightEight: top,
    increment,
  };
}

export function computeMurreyMathLinesV6Overlay({
  candles,
  symbol,
  frameSize = DEFAULT_FRAME_SIZE,
  frameMultiplier = DEFAULT_FRAME_MULTIPLIER,
  sourceMode = DEFAULT_SOURCE_MODE,
  addExtendedLines = DEFAULT_ADD_EXTENDED_LINES,
  extraLinesCount = DEFAULT_EXTRA_LINES_COUNT,
}: ComputeMurreyMathLinesV6OverlayParams): MurreyMathLinesV6OverlayResult | null {
  if (!Array.isArray(candles) || candles.length === 0) {
    return null;
  }

  const validCandles = candles.filter(
    (candle) =>
      Number.isFinite(candle.open) &&
      Number.isFinite(candle.high) &&
      Number.isFinite(candle.low) &&
      Number.isFinite(candle.close),
  );

  const normalizedFrameSize = clampFrameSize(frameSize);
  const normalizedMultiplier = frameMultiplier === 1.5 || frameMultiplier === 2 ? frameMultiplier : 1;
  const effectiveFrameSize = Math.max(8, Math.round(normalizedFrameSize * normalizedMultiplier));

  if (validCandles.length < effectiveFrameSize + 1) {
    return null;
  }

  const frameCandles = validCandles.slice(-(effectiveFrameSize + 1), -1);
  let frameHigh = Number.NEGATIVE_INFINITY;
  let frameLow = Number.POSITIVE_INFINITY;

  for (const candle of frameCandles) {
    const sourceHigh = sourceMode === 'openClose' ? Math.max(candle.open, candle.close) : candle.high;
    const sourceLow = sourceMode === 'openClose' ? Math.min(candle.open, candle.close) : candle.low;

    if (sourceHigh > frameHigh) {
      frameHigh = sourceHigh;
    }
    if (sourceLow < frameLow) {
      frameLow = sourceLow;
    }
  }

  const core = computeMurreyCore(frameHigh, frameLow);
  if (!core) {
    return null;
  }

  const levels: MurreyMathLevel[] = [];
  const normalizedExtraLinesCount = normalizeExtraLinesCount(extraLinesCount);
  const extendedPerSide = normalizedExtraLinesCount / 2;

  if (addExtendedLines) {
    for (let offset = extendedPerSide; offset >= 1; offset -= 1) {
      levels.push({
        key: `minus-${offset}`,
        label: `-${offset}`,
        price: core.zeroEight - core.increment * offset,
        kind: 'extended',
        isExtended: true,
      });
    }
  }

  for (let index = 0; index <= 8; index += 1) {
    levels.push({
      key: `${index}-8`,
      label: `${index}/8`,
      price: core.zeroEight + core.increment * index,
      kind: levelKind(index),
      isExtended: false,
    });
  }

  if (addExtendedLines) {
    for (let offset = 1; offset <= extendedPerSide; offset += 1) {
      levels.push({
        key: `plus-${offset}`,
        label: `+${offset}`,
        price: core.eightEight + core.increment * offset,
        kind: 'extended',
        isExtended: true,
      });
    }
  }

  const latestClose = validCandles[validCandles.length - 1].close;
  let nearestLevel: MurreyMathLevel | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const level of levels) {
    const distance = Math.abs(level.price - latestClose);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestLevel = level;
    }
  }

  const precision = inferPricePrecision(validCandles);
  const nearestPrice = nearestLevel?.price;

  return {
    levels,
    zeroEight: core.zeroEight,
    oneEight: core.zeroEight + core.increment,
    threeEight: core.zeroEight + core.increment * 3,
    fiveEight: core.zeroEight + core.increment * 5,
    sevenEight: core.zeroEight + core.increment * 7,
    eightEight: core.eightEight,
    nearestLabel: nearestLevel?.label,
    nearestPrice,
    info: `MML v6 ${symbol} nearest: ${nearestLevel?.label ?? 'n/a'} @ ${
      nearestPrice === undefined ? 'n/a' : nearestPrice.toFixed(precision)
    }`,
  };
}
